
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const logWithTime = (message: string, data?: any) => {
  const timestamp = new Date().toISOString();
  if (data) {
    console.log(`${message} [${timestamp}]`, data);
  } else {
    console.log(`${message} [${timestamp}]`);
  }
};

// Use a single consistent bucket name throughout the application
const REAGENT_IMAGES_BUCKET = 'reagent-images';

export const checkBucketExists = async (bucketName: string): Promise<boolean> => {
  try {
    logWithTime(`Checking if bucket '${bucketName}' exists`);
    
    // Try to get bucket details directly
    const { data, error } = await supabase.storage.getBucket(bucketName);
    
    if (error) {
      // StorageError doesn't have a code property, so check the message instead
      if (error.message && error.message.includes("not found")) {
        logWithTime(`Bucket '${bucketName}' not found`);
        return false;
      }
      
      // For any other error, log it but assume the bucket might not exist
      logWithTime(`Error checking bucket '${bucketName}':`, error);
      return false;
    }
    
    // If we got data back, the bucket exists
    logWithTime(`Bucket '${bucketName}' exists:`, data);
    return true;
  } catch (error) {
    logWithTime("Error in checkBucketExists:", error);
    return false;
  }
};

export const getPublicUrl = (bucket: string, path: string): string => {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data.publicUrl;
};

export const uploadFile = async (
  file: File,
  bucketName: string,
  filePath: string
): Promise<string | null> => {
  try {
    logWithTime(`Starting file upload to ${bucketName}/${filePath}`);
    
    if (!file) {
      logWithTime("No file provided for upload");
      return null;
    }
    
    if (file.size > 10485760) { // 10MB for mobile compatibility
      logWithTime("File size exceeds limit:", file.size);
      throw new Error("File size exceeds 10MB limit");
    }
    
    // Try uploading without checking bucket existence first
    logWithTime("Uploading file:", { name: file.name, size: file.size, type: file.type });
    
    const { error: uploadError, data } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true
      });
    
    if (uploadError) {
      // If we get a bucket not found error, show a specific message
      if (uploadError.message && uploadError.message.includes("bucket not found")) {
        logWithTime("Upload failed because bucket does not exist:", uploadError);
        toast.error(`Storage bucket '${bucketName}' is not available. Contact an administrator.`);
        return null;
      }
      
      logWithTime("Upload error:", uploadError);
      throw new Error(`File upload failed: ${uploadError.message}`);
    }
    
    if (!data) {
      logWithTime("Upload succeeded but no data returned");
      return null;
    }
    
    const publicUrl = getPublicUrl(bucketName, filePath);
    logWithTime("File uploaded successfully, public URL:", publicUrl);
    return publicUrl;
  } catch (error) {
    logWithTime("Error in uploadFile:", error);
    throw error;
  }
};

// Export the bucket name as a constant
export const REAGENT_IMAGES_BUCKET_NAME = REAGENT_IMAGES_BUCKET;
